import React from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useLocation } from 'react-router-dom';
import { IcoChevR, IcoBell, IcoLife } from './icons';
import { findProduct, SUPER_ADMIN } from './products';
import { SECTION_BY_ID } from '../super-admin/registry';
import { PLATFORM_NAV } from './BugZeroSidebar';
import SnStatusChip from './SnStatusChip';

// Best-effort breadcrumb derived from the current path + context.
// We don't try to mirror every legacy route — just give the user the product
// name, a chevron, and a friendly label for where they are.
const friendlyLabelForPath = pathname => {
  if (pathname === '/' || pathname === '/platform') return 'Home';
  const segs = pathname.split('/').filter(Boolean);
  if (segs[0] === 'platform' && segs[1]) {
    const node = PLATFORM_NAV.find(n => n.id === segs[1]);
    if (!node) return 'Settings';
    // Sub-page of an expandable section (e.g. /platform/members/roles).
    // The landing page (/platform/members) has no segs[2] and keeps the bare label.
    const child = segs[2] && Array.isArray(node.children) ? node.children.find(c => c.to === pathname) : null;
    return child ? `${node.label} · ${child.label}` : node.label;
  }
  if (segs[0] === 'plan') return 'Projects';
  if (segs[0] === 'predict')
    return (
      {
        watch: 'Watch list',
        outcomes: 'Outcomes',
        model: 'Model and scoring',
        coverage: 'Coverage',
        settings: 'Settings'
      }[segs[1]] || 'Overview'
    );
  if (segs[0] === 'prevent') return 'Dashboard';
  if (segs[0] === 'vendor-health') return 'Vendor health';
  if (segs[0] === 'vendors') return 'Vendors';
  if (segs[0] === 'integrations') return 'Integrations';
  if (segs[0] === 'users') return 'Admin';
  if (segs[0] === 'admin') return 'Admin';
  if (segs[0] === 'support') return 'Support';
  if (segs[0] === 'documentation') return 'Documentation';
  if (segs[0] === 'profile') return 'Profile';
  return segs[segs.length - 1]
    .split('-')
    .map(s => s.charAt(0).toUpperCase() + s.slice(1))
    .join(' ');
};

const BugZeroTopBar = ({ contextId, platformSection }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isPlatform = contextId === 'platform';
  const isSuperAdmin = contextId === SUPER_ADMIN.id;
  const product = !isPlatform && !isSuperAdmin ? findProduct(contextId) : null;

  const productLabel = isPlatform ? 'Platform' : isSuperAdmin ? SUPER_ADMIN.short : product.short;
  const isPlatformHome = isPlatform && platformSection === 'home';
  const tail = friendlyLabelForPath(location.pathname);

  return (
    <header
      style={{
        height: 48,
        background: 'var(--bg-elev)',
        borderBottom: '1px solid var(--line)',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '0 24px',
        flexShrink: 0
      }}
    >
      <div className="bz-row" style={{ gap: 8, color: 'var(--ink-muted)', fontSize: 'var(--fs-sm)' }}>
        {isSuperAdmin ? (
          <>
            <span
              onClick={() => navigate(SUPER_ADMIN.rootPath)}
              style={{ color: 'var(--ink-muted)', cursor: 'pointer' }}
            >
              {SUPER_ADMIN.short}
            </span>
            <IcoChevR size={11} />
            <span style={{ color: 'var(--ink)', fontWeight: 500 }}>
              {(SECTION_BY_ID[location.pathname.split('/')[2]] || SECTION_BY_ID.overview).label}
            </span>
          </>
        ) : isPlatformHome ? (
          <>
            <span style={{ color: 'var(--ink)', fontWeight: 500 }}>{productLabel}</span>
            <IcoChevR size={11} />
            <span>{tail}</span>
          </>
        ) : isPlatform ? (
          <>
            <span
              onClick={() => navigate('/')}
              style={{ color: 'var(--ink-muted)', textDecoration: 'none', cursor: 'pointer' }}
            >
              Platform
            </span>
            <IcoChevR size={11} />
            <span
              onClick={() => navigate('/')}
              style={{ color: 'var(--ink-muted)', textDecoration: 'none', cursor: 'pointer' }}
            >
              Settings
            </span>
            <IcoChevR size={11} />
            <span style={{ color: 'var(--ink)', fontWeight: 500 }}>{tail}</span>
          </>
        ) : (
          <>
            <span style={{ color: 'var(--ink)', fontWeight: 500 }}>{productLabel}</span>
            <IcoChevR size={11} />
            <span>{tail}</span>
          </>
        )}
      </div>
      <div className="bz-grow" />
      {/* Global search (⌘K) is hidden until a search backend exists — see COMING_SOON.md. */}
      <SnStatusChip />
      <button type="button" className="bz-btn bz-btn--ghost bz-btn--sm" title="Notifications" style={{ padding: 6 }}>
        <IcoBell size={15} />
      </button>
      <button
        type="button"
        className="bz-btn bz-btn--ghost bz-btn--sm"
        title="Help"
        style={{ padding: 6 }}
        onClick={() => navigate('/support/get-help')}
      >
        <IcoLife size={15} />
      </button>
    </header>
  );
};

BugZeroTopBar.propTypes = {
  contextId: PropTypes.string.isRequired,
  platformSection: PropTypes.string
};

export default BugZeroTopBar;
